import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import PendingUpdate from 'src/assets/images/PendingUpdate.svg'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CollapsibleMiddleBox from 'src/components/CollapsibleMiddleBox/CollapsibleMiddleBox'
import RecurrenceAmountAndFrequencyLabel from 'src/components/RecurrenceAmountAndFrequencyLabel/RecurrenceAmountAndFrequencyLabel'
import RecurrenceEndingLabel from 'src/components/RecurrenceEndingLabel/RecurrenceEndingLabel'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import { RecurrenceTerminator } from 'src/models/recurrence.models'
import {
  VirtualCardPendingRevisionModel,
  VirtualCardSecureModel,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import { updateVirtualCardUrl } from 'src/navigation/urls'
import { css } from 'styled-components'

interface PendingModificationBoxProps {
  className?: string
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions?: VirtualCardPermissionsModel
  timezone: string
  pendingRevision: VirtualCardPendingRevisionModel
}

const InfoStack: React.FC<{
  header: React.ReactElement
  body: React.ReactElement | string | undefined
}> = (props): React.ReactElement => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;

        &:nth-child(n + 2) {
          margin-left: 48px;
        }
      `}
    >
      <span
        css={css`
          ${getFontCss(
            14,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {props.header}
      </span>
      <span
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )}
          margin-top: 4px;
        `}
      >
        {props.body}
      </span>
    </div>
  )
}

const PendingModificationBox: React.FC<PendingModificationBoxProps> = (
  props,
): React.ReactElement => {
  const topContent = (
    <div
      css={css`
        display: flex;
        align-items: center;

        padding: ${styleConstants.heights.cardPadding}
          ${styleConstants.widths.cardPaddingXSMUp};
        ${getBreakpointOnly(breakpointSizes.XXSM)(css`
          padding: ${styleConstants.heights.cardPadding}
            ${styleConstants.widths.cardPaddingXXSMOnly};
        `)}
      `}
    >
      <img
        src={PendingUpdate}
        alt={''}
        css={css`
          margin-right: 24px;
          height: 60px;
        `}
      />
      <span
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.highlight,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        <Trns source={'Request Awaiting Approval'} />
      </span>
    </div>
  )

  const middleContent = (
    <div
      css={css`
        display: flex;
        flex-direction: column;

        padding: 0px ${styleConstants.widths.cardPaddingXSMUp};
        ${getBreakpointDown(breakpointSizes.XXSM)(css`
          padding: 0px ${styleConstants.widths.cardPaddingXXSMOnly};
        `)}
      `}
    >
      <div
        css={css`
          display: flex;
          margin-top: ${styleConstants.heights.cardPadding};
          margin-bottom: ${styleConstants.heights.cardPadding};
        `}
      >
        {props.pendingRevision.balance !== undefined &&
          !props.virtualCard.isIssuerCycle && (
            <InfoStack
              header={<Trns source={'Available Credit Requested'} />}
              body={
                <Crncy
                  currency={props.virtualCard.currency}
                  amount={props.pendingRevision.balance}
                />
              }
            />
          )}
        {props.pendingRevision.balance !== undefined &&
          props.virtualCard.isIssuerCycle && (
            <InfoStack
              header={<Trns source={'Monthly Credit Limit Requested'} />}
              body={
                <Crncy
                  currency={props.virtualCard.currency}
                  amount={props.pendingRevision.balance}
                  maximumFractionDigits={0}
                />
              }
            />
          )}
        {/* TODO: revert back to activeUntil */}
        {props.pendingRevision.validToTimezonedString(props.timezone) !==
          undefined && (
          <InfoStack
            header={<Trns source={'Active Until Date Requested'} />}
            body={props.pendingRevision.validToTimezonedString(props.timezone)}
          />
        )}
        {(props.pendingRevision.recurs === true ||
          props.pendingRevision.recurs === false) && (
          <InfoStack
            header={<Trns source={'Card Type Requested'} />}
            body={
              props.pendingRevision.recurs === true ? (
                <Trns source={'Recurring'} />
              ) : (
                <Trns source={'Standard'} />
              )
            }
          />
        )}
        {props.pendingRevision.recurrence !== undefined && (
          <InfoStack
            header={<Trns source={'Frequency Requested'} />}
            body={
              <Trns
                source={
                  props.pendingRevision.recurrence.terminator ===
                  RecurrenceTerminator.Count
                    ? '${recurrenceAmountAndFrequencyLabel}. ${recurrenceEndingLabel}.'
                    : '${recurrenceAmountAndFrequencyLabel}, ${recurrenceEndingLabel}.'
                }
                expressions={{
                  recurrenceAmountAndFrequencyLabel: (
                    <RecurrenceAmountAndFrequencyLabel
                      period={props.pendingRevision.recurrence.period}
                      balance={props.pendingRevision.recurrence.balance}
                      interval={props.pendingRevision.recurrence.interval}
                      byWeekDay={props.pendingRevision.recurrence.byWeekDay}
                      byMonthDay={props.pendingRevision.recurrence.byMonthDay}
                      byYearDay={props.pendingRevision.recurrence.byYearDay}
                      timezone={props.virtualCard.timezone}
                      currency={props.virtualCard.currency}
                    />
                  ),
                  recurrenceEndingLabel: (
                    <RecurrenceEndingLabel
                      terminator={props.pendingRevision.recurrence.terminator}
                      count={props.pendingRevision.recurrence.count}
                      untilMoment={props.pendingRevision.recurrence.untilTimezonedMoment(
                        props.timezone,
                      )}
                    />
                  ),
                }}
              />
            }
          />
        )}
      </div>
      {props.virtualCardPermissions?.canUpdateVirtualCard && (
        <div
          css={css`
            margin-bottom: ${styleConstants.heights.cardPadding};
          `}
        >
          <Button
            linkProps={{
              className: 'action-btn-lnk',
              to: updateVirtualCardUrl(props.virtualCard.id),
              ariaLabelSource: 'Approve or Deny Request',
            }}
            css={css`
              width: 215px;
            `}
            design={ButtonDesigns.designCta}
            type={ButtonTypes.button}
            text={<Trns source={'Approve or Deny Request'} />}
          />
        </div>
      )}
    </div>
  )

  const closedBottomLabel = <Trns source={'Request Details'} />
  const openBottomLabel = <Trns source={'Hide Request Details'} />

  return (
    <CollapsibleMiddleBox
      className={props.className}
      topContent={topContent}
      middleContent={middleContent}
      bottomContent={{
        closedBottomLabel: closedBottomLabel,
        openBottomLabel: openBottomLabel,
      }}
    />
  )
}

export default PendingModificationBox
